'use client';

import type { ReactNode } from 'react';

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

type DevToolsContextType = {
  portalTarget: HTMLElement | null;
};

const DevToolsContext = createContext<DevToolsContextType | null>(null);

export function useDevToolsPortal() {
  const context = useContext(DevToolsContext);
  if (!context) {
    throw new Error('useDevToolsPortal must be used within DevToolsProvider');
  }
  return context;
}

type Props = {
  children: ReactNode;
};

export default function DevToolsProvider({ children }: Props) {
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      setPortalTarget(containerRef.current);
    }
  }, []);

  const contextValue = useMemo(() => ({ portalTarget }), [portalTarget]);

  if (process.env.NODE_ENV !== 'development') {
    return <>{children}</>;
  }

  return (
    <DevToolsContext.Provider value={contextValue}>
      {children}
      <div
        ref={containerRef}
        className="fixed right-4 bottom-16 z-[9999] flex flex-col items-end gap-4"
        id="devtools-portal-container"
      />
    </DevToolsContext.Provider>
  );
}
